import { Bell, Clock, ShieldWarning, SlidersHorizontal } from "@phosphor-icons/react";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import api from "../lib/api";
import { confirmProductionWrite } from "../lib/diagnostics";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./ui/alert-dialog";

const MODE_OPTIONS = [
  ["trusted_auto", "Trusted automation"],
  ["apply_only", "Apply only"],
  ["apply_verify", "Apply + verify"],
  ["apply_verify_publish", "Apply + verify + publish"],
  ["dry_run_only", "Dry-run only"],
  ["scan_only", "Scan-only diagnostics"],
  ["manual_only", "Manual only"],
  ["paused", "Paused"],
  ["disabled", "Disabled"],
  ["next_candidate", "Next candidate"],
];

const TRUSTED_MODE_OPERATOR_KEYS = new Set(["dcg", "llf", "kilted", "ukcc", "bounty", "storm", "redline", "prizeparadise", "lincolnshire", "drift", "palladium", "pristine", "caddy", "hotcomps", "bright", "apex", "jammy", "elite", "revcomps", "botb", "7days", "click", "mckinney", "salexmo"]);

const ACTION_FIELDS = [
  ["scan_enabled", "Scan"],
  ["dry_run_enabled", "Dry-run"],
  ["apply_enabled", "Apply"],
  ["verify_enabled", "Verify"],
  ["publish_enabled", "Publish"],
  ["expire_enabled", "Expire"],
];
const BOOLEAN_SETTING_FIELDS = new Set([
  ...ACTION_FIELDS.map(([field]) => field),
  "scheduler_enabled",
  "notifications_enabled",
]);

const LOCKED_MODES = new Set(["paused", "disabled", "next_candidate"]);
const BOTB_ACTION_FIELDS = new Set(["scan_enabled", "expire_enabled"]);
const SEVENDAYS_ACTION_FIELDS = new Set([
  "scan_enabled",
  "dry_run_enabled",
  "apply_enabled",
  "verify_enabled",
  "publish_enabled",
  "expire_enabled",
]);
const CLICK_ACTION_FIELDS = new Set([
  "scan_enabled",
  "dry_run_enabled",
  "apply_enabled",
  "verify_enabled",
  "publish_enabled",
  "expire_enabled",
]);
const JFD_ACTION_FIELDS = new Set(["scan_enabled", "dry_run_enabled"]);
const FEELGOOD_ACTION_FIELDS = new Set(["scan_enabled", "dry_run_enabled"]);
const ASPIRE_ACTION_FIELDS = new Set([
  "scan_enabled",
  "dry_run_enabled",
  "apply_enabled",
  "verify_enabled",
  "publish_enabled",
  "expire_enabled",
]);
const MCKINNEY_ACTION_FIELDS = new Set(ASPIRE_ACTION_FIELDS);
const LLF_ACTION_FIELDS = new Set(ASPIRE_ACTION_FIELDS);
const KILTED_ACTION_FIELDS = new Set(ASPIRE_ACTION_FIELDS);

export function parseNumberSettingValue(raw, { min = 0, max = null, label = "Value" } = {}) {
  const text = String(raw ?? "").trim();
  if (text === "") return { skip: true };
  const value = Number(text);
  if (!Number.isInteger(value)) {
    return { error: `${label} must be a whole number.` };
  }
  if (value < min || (max != null && value > max)) {
    return {
      error: max == null
        ? `${label} must be ${min} or higher.`
        : `${label} must be between ${min} and ${max}.`,
    };
  }
  return { value };
}

export default function OperatorAutomationSettingsCard({
  operator,
  settings,
  diag,
  onUpdated,
}) {
  const [revReadiness, setRevReadiness] = useState(null);
  const [revReadinessBusy, setRevReadinessBusy] = useState(false);
  const [booleanSettingOverrides, setBooleanSettingOverrides] = useState({});
  const [pendingBooleanSettings, setPendingBooleanSettings] = useState({});
  const [settingsUpdateError, setSettingsUpdateError] = useState("");
  const [settingsWritePending, setSettingsWritePending] = useState(false);
  const [productionConfirmation, setProductionConfirmation] = useState(null);
  const productionConfirmationResolver = useRef(null);
  const settingsWritePendingRef = useRef(false);
  const settingsRequestTokenRef = useRef(0);
  const selectedOperatorRef = useRef(operator?.key);
  const operatorKey = operator?.key;
  const isRevComps = operatorKey === "revcomps";
  const isBotb = operatorKey === "botb";
  const isSevenDays = operatorKey === "7days";
  const isClick = operatorKey === "click";
  const isJonnyFromDonny = operatorKey === "jonnyfromdonny";
  const isFeelGood = operatorKey === "feelgood";
  const isAspire = operatorKey === "aspire";
  const isMcKinney = operatorKey === "mckinney";
  const isLlf = operatorKey === "llf";
  const isKilted = operatorKey === "kilted";
  const isUkcc = operatorKey === "ukcc";
  const isBounty = operatorKey === "bounty";
  const isStorm = operatorKey === "storm";
  const isRedline = operatorKey === "redline";
  const isPrizeParadise = operatorKey === "prizeparadise";
  const isLincolnshire = operatorKey === "lincolnshire";
  const isDrift = operatorKey === "drift";
  const isPalladium = operatorKey === "palladium";
  const isPristine = operatorKey === "pristine";
  const isCaddy = operatorKey === "caddy";
  const isHotComps = operatorKey === "hotcomps";
  const isBright = operatorKey === "bright";
  const isApex = operatorKey === "apex";
  const isJammy = operatorKey === "jammy";
  const isSalexmo = operatorKey === "salexmo";
  const isExactSetOperator = isKilted || isUkcc || isBounty || isStorm || isRedline || isPrizeParadise || isLincolnshire || isDrift || isPalladium || isPristine || isCaddy || isHotComps || isBright || isApex || isJammy || isSalexmo;
  const exactSetPrefix = isUkcc ? "ukcc" : isBounty ? "bounty" : isStorm ? "storm" : isRedline ? "redline" : isPrizeParadise ? "prizeparadise" : isLincolnshire ? "lincolnshire" : isDrift ? "drift" : isPalladium ? "palladium" : isPristine ? "pristine" : isCaddy ? "caddy" : isHotComps ? "hotcomps" : isBright ? "bright" : isApex ? "apex" : isJammy ? "jammy" : isSalexmo ? "salexmo" : "kilted";
  const exactSetPublishGateAllowed = settings?.[`${exactSetPrefix}_publish_gate_allowed`] === true;
  const exactSetLiveSetId = (
    exactSetPublishGateAllowed
      ? settings?.[`${exactSetPrefix}_publish_gate_live_set_id`]
      : null
  ) || settings?.[`latest_successful_${exactSetPrefix}_live_set_id`];
  const exactSetIdentityCount = exactSetPublishGateAllowed
    ? settings?.[`${exactSetPrefix}_publish_gate_identity_count`]
    : settings?.[`latest_successful_${exactSetPrefix}_live_set_identity_count`];
  const exactSetIdentityDigest = (
    exactSetPublishGateAllowed
      ? settings?.[`${exactSetPrefix}_publish_gate_identity_digest`]
      : null
  ) || settings?.[`latest_successful_${exactSetPrefix}_live_set_identity_digest`];
  const exactSetOperatorName = operator?.name || (isUkcc ? "UKCC Competitions" : isBounty ? "Bounty Competitions" : isStorm ? "Storm Competitions" : isRedline ? "Redline Competitions" : isPrizeParadise ? "Prize Paradise" : isLincolnshire ? "Lincolnshire Competitions" : isDrift ? "Drift Competitions" : isCaddy ? "Caddy Comps" : isHotComps ? "Hot Comps" : isBright ? "Bright Competitions" : isApex ? "Apex Competitions" : isJammy ? "JAMMY" : "Kilted Competitions");
  const mode = settings?.mode || operator.mode || "manual_only";
  const locked = LOCKED_MODES.has(mode);
  const loading = !settings;
  const controlPlaneAvailable = settings?.control_plane_state === "available";
  const settingsGeneration = settings?.settings_generation;
  const persistedControls = settings?.persisted_controls || {};
  const persistedEnabledControls = Object.entries(persistedControls)
    .filter(([field, value]) => field !== "mode" && value === true)
    .map(([field]) => field.replace(/_enabled$/, ""))
    .sort();
  const settingsControlsDisabled = (
    loading
    || !controlPlaneAvailable
    || settings?.migration_required === true
    || !Number.isInteger(settingsGeneration)
    || settingsWritePending
  );
  const settleProductionConfirmation = useCallback((accepted) => {
    const resolve = productionConfirmationResolver.current;
    productionConfirmationResolver.current = null;
    setProductionConfirmation(null);
    resolve?.(accepted);
  }, []);
  const confirmProductionWriteInApp = useCallback((message) => {
    if (!diag?.is_production) {
      return Promise.resolve(confirmProductionWrite(diag, message));
    }
    if (productionConfirmationResolver.current) {
      productionConfirmationResolver.current(false);
    }
    return new Promise((resolve) => {
      productionConfirmationResolver.current = resolve;
      setProductionConfirmation({
        message,
        databaseName: diag.db_name || "production",
      });
    });
  }, [diag]);
  useEffect(() => {
    setRevReadiness(null);
  }, [operator?.key, settings?.latest_successful_rev_live_set_id, settings?.updated_at]);
  useEffect(() => {
    selectedOperatorRef.current = operatorKey;
    settingsRequestTokenRef.current += 1;
    settingsWritePendingRef.current = false;
    setSettingsWritePending(false);
    setBooleanSettingOverrides({});
    setPendingBooleanSettings({});
  }, [operatorKey]);
  useEffect(() => {
    if (!settingsWritePendingRef.current) {
      setBooleanSettingOverrides({});
      setPendingBooleanSettings({});
    }
  }, [operatorKey, settingsGeneration]);
  useEffect(() => {
    setSettingsUpdateError("");
  }, [operatorKey]);
  useEffect(() => () => {
    if (productionConfirmationResolver.current) {
      productionConfirmationResolver.current(false);
      productionConfirmationResolver.current = null;
    }
  }, []);
  if (!operator) return null;

  const settingChecked = (field) => {
    if (Object.prototype.hasOwnProperty.call(booleanSettingOverrides, field)) {
      return booleanSettingOverrides[field] === true;
    }
    if (Object.prototype.hasOwnProperty.call(persistedControls, field)) {
      return persistedControls[field] === true;
    }
    return settings?.[field] === true;
  };

  const patchSettings = async (patch) => {
    if (
      !settings?.operator_key
      || !controlPlaneAvailable
      || !Number.isInteger(settingsGeneration)
      || settingsWritePendingRef.current
    ) return { ok: false };
    if (
      patch.publish_enabled === true
      && !await confirmProductionWriteInApp(
        `Enable trusted publishing for ${operator.name}. Publishing will still require the backend public gate.`,
      )
    ) {
      return { ok: false, cancelled: true };
    }
    if (
      patch.expire_enabled === true
      && !await confirmProductionWriteInApp(
        isBotb
          ? "Enable BOTB guarded live expiry. Live expiry hides stale public rows only, never deletes raw records, and runs only through backend safety gates."
          : isClick
          ? "Enable Click guarded live expiry. Live expiry hides only published Click records with current-detail closed or past-draw evidence, never deletes raw records, and runs only through backend safety gates."
          : `Enable trusted expiry/hide actions for ${operator.name}. Expiry guardrails still apply.`,
      )
    ) {
      return { ok: false, cancelled: true };
    }
    const booleanEntries = Object.entries(patch).filter(
      ([field, value]) => BOOLEAN_SETTING_FIELDS.has(field) && typeof value === "boolean",
    );
    setSettingsUpdateError("");
    settingsWritePendingRef.current = true;
    setSettingsWritePending(true);
    const requestToken = settingsRequestTokenRef.current + 1;
    settingsRequestTokenRef.current = requestToken;
    const requestOperatorKey = settings.operator_key;
    if (booleanEntries.length > 0) {
      setBooleanSettingOverrides((current) => ({
        ...current,
        ...Object.fromEntries(booleanEntries),
      }));
      setPendingBooleanSettings((current) => ({
        ...current,
        ...Object.fromEntries(booleanEntries.map(([field]) => [field, true])),
      }));
    }
    try {
      const { data } = await api.patch(
        `/admin/operator-automation-settings/${settings.operator_key}`,
        {
          ...patch,
          expected_settings_generation: settingsGeneration,
        },
      );
      if (
        settingsRequestTokenRef.current !== requestToken
        || selectedOperatorRef.current !== requestOperatorKey
      ) return { ok: false, stale: true };
      const rejectedBooleanEntries = booleanEntries.filter(
        ([field, value]) => data?.[field] !== value,
      );
      if (booleanEntries.length > 0) {
        setBooleanSettingOverrides((current) => ({
          ...current,
          ...Object.fromEntries(booleanEntries.map(([field]) => [
            field,
            data?.[field] === true,
          ])),
        }));
        setPendingBooleanSettings((current) => {
          const next = { ...current };
          for (const [field] of booleanEntries) delete next[field];
          return next;
        });
      }
      onUpdated?.(data);
      if (rejectedBooleanEntries.length > 0) {
        const message = "The server did not persist the requested automation setting.";
        setSettingsUpdateError(message);
        toast.error(`Settings update failed: ${message}`);
        return { ok: false, data, error: message };
      }
      toast.success("Operator automation settings updated.");
      return { ok: true, data };
    } catch (error) {
      if (
        settingsRequestTokenRef.current !== requestToken
        || selectedOperatorRef.current !== requestOperatorKey
      ) return { ok: false, stale: true };
      if (booleanEntries.length > 0) {
        setBooleanSettingOverrides((current) => {
          const next = { ...current };
          for (const [field] of booleanEntries) delete next[field];
          return next;
        });
        setPendingBooleanSettings((current) => {
          const next = { ...current };
          for (const [field] of booleanEntries) delete next[field];
          return next;
        });
      }
      const detail = error.response?.data?.detail?.error
        || error.response?.data?.detail
        || error.message;
      if (error.response?.status === 409) {
        try {
          const { data: authoritative } = await api.get(
            `/admin/operator-automation-settings/${requestOperatorKey}`,
          );
          if (
            settingsRequestTokenRef.current === requestToken
            && selectedOperatorRef.current === requestOperatorKey
          ) {
            setBooleanSettingOverrides({});
            setPendingBooleanSettings({});
            onUpdated?.(authoritative);
          }
        } catch (_reloadError) {
          // The conflict remains visible even if the authoritative reload fails.
        }
      }
      const message = `Settings update failed: ${detail}`;
      setSettingsUpdateError(message);
      toast.error(message);
      return { ok: false, error: detail };
    } finally {
      if (
        settingsRequestTokenRef.current === requestToken
        && selectedOperatorRef.current === requestOperatorKey
      ) {
        settingsWritePendingRef.current = false;
        setSettingsWritePending(false);
      }
    }
  };

  const migrateLegacySettings = async () => {
    if (
      settings?.migration_required !== true
      || !Number.isInteger(settingsGeneration)
      || settingsGeneration < 0
    ) return;
    if (!await confirmProductionWriteInApp(
      `Adopt the exact persisted automation intent for ${operator.name} as generation ${settingsGeneration + 1}. This migration changes no capability switch; it only makes the displayed persisted settings authoritative.`,
    )) return;
    await patchSettings({ confirm_legacy_settings_migration: true });
  };

  const checkRevReadiness = async () => {
    const liveSetId = String(settings?.latest_successful_rev_live_set_id || "").trim();
    if (!isRevComps || !liveSetId) {
      toast.error("Rev scheduler readiness requires the latest successful live-set ID.");
      return;
    }
    setRevReadiness(null);
    setRevReadinessBusy(true);
    try {
      const { data } = await api.post(
        "/admin/operator-automation/revcomps/scheduler-readiness",
        { expected_live_set_id: liveSetId },
      );
      setRevReadiness(data);
      toast[data.ready ? "success" : "error"](`Rev scheduler readiness: ${data.readiness}.`);
    } catch (error) {
      setRevReadiness(null);
      toast.error(
        "Rev scheduler readiness failed: "
          + (error.response?.data?.detail?.error || error.response?.data?.detail || error.message),
      );
    } finally {
      setRevReadinessBusy(false);
    }
  };

  const lastSummary = settings?.last_run_summary || {};

  return (
    <div
      className="rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-4"
      data-testid="operator-automation-settings-card"
    >
      <AlertDialog
        open={productionConfirmation != null}
        onOpenChange={(open) => {
          if (!open && productionConfirmationResolver.current) {
            settleProductionConfirmation(false);
          }
        }}
      >
        <AlertDialogContent data-testid="operator-automation-settings-production-confirmation">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm production settings change</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p className="whitespace-pre-wrap text-white/80">
                  {productionConfirmation?.message}
                </p>
                <p>
                  This will modify the connected production database (
                  {productionConfirmation?.databaseName || "production"}).
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              type="button"
              onClick={() => settleProductionConfirmation(false)}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              type="button"
              onClick={() => settleProductionConfirmation(true)}
              data-testid="operator-automation-settings-confirm-production-change"
            >
              Confirm settings change
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-semibold text-white">
            <SlidersHorizontal size={17} weight="bold" />
            Automation Settings
          </div>
          <div className="text-[11px] text-white/55 mt-1">
            Database-backed controls for this selected operator.
          </div>
        </div>
        {loading ? (
          <span className="text-[11px] text-white/40">Loading...</span>
        ) : (
          <span className="px-2 py-0.5 rounded border border-white/10 bg-white/[0.04] text-[10px] uppercase tracking-wider text-white/60">
            {settings.updated_by ? `Updated by ${settings.updated_by}` : "Default settings"}
          </span>
        )}
      </div>

      {settingsUpdateError && (
        <div
          role="alert"
          className="rounded border border-red-400/30 bg-red-400/10 px-3 py-2 text-[11px] text-red-100"
          data-testid="operator-automation-settings-update-error"
        >
          {settingsUpdateError}
        </div>
      )}

      {!loading && !controlPlaneAvailable && (
        <div
          role="alert"
          className="rounded border border-red-400/30 bg-red-400/10 px-3 py-2 text-[11px] text-red-100"
          data-testid="operator-automation-control-plane-unavailable"
        >
          Settings control plane {settings?.control_plane_state || "unavailable"}.
          Mutation controls remain disabled until authoritative settings are available.
        </div>
      )}

      {!loading && controlPlaneAvailable && settings?.migration_required === true && (
        <div
          role="alert"
          className="rounded border border-amber-400/30 bg-amber-400/10 px-3 py-2 text-[11px] text-amber-100"
          data-testid="operator-automation-settings-migration-required"
        >
          <div>
            Legacy settings require an explicit generation-bound update. Persisted intent is shown;
            mutation controls remain disabled.
          </div>
          <button
            type="button"
            onClick={migrateLegacySettings}
            disabled={
              settingsWritePending
              || !Number.isInteger(settingsGeneration)
              || settingsGeneration < 0
            }
            data-testid="operator-automation-settings-migrate-legacy"
            className="mt-2 rounded border border-amber-300/40 bg-amber-200/10 px-3 py-1.5 text-[11px] font-semibold text-amber-50 disabled:opacity-50"
          >
            {settingsWritePending ? "Adopting persisted intent..." : "Adopt persisted intent"}
          </button>
        </div>
      )}

      {!loading && (
        <dl
          className="grid sm:grid-cols-2 lg:grid-cols-4 gap-2 text-[11px]"
          data-testid="operator-automation-authority-states"
        >
          <div className="rounded border border-white/10 bg-white/[0.03] px-3 py-2">
            <dt className="text-white/45">Persisted intent</dt>
            <dd>
              {settings?.settings_present === true
                ? `generation ${settingsGeneration} · ${persistedEnabledControls.join(", ") || "none enabled"}`
                : "not available"}
            </dd>
          </div>
          <div className="rounded border border-white/10 bg-white/[0.03] px-3 py-2">
            <dt className="text-white/45">Capability</dt>
            <dd>{Object.entries(settings?.operator_capabilities || {}).filter(([, value]) => value === true).map(([key]) => key.replace(/_supported$/, "")).join(", ") || "none declared"}</dd>
          </div>
          <div className="rounded border border-white/10 bg-white/[0.03] px-3 py-2">
            <dt className="text-white/45">Readiness</dt>
            <dd>{settings?.readiness?.state || "not evaluated"}</dd>
          </div>
          <div className="rounded border border-white/10 bg-white/[0.03] px-3 py-2">
            <dt className="text-white/45">Effective authority</dt>
            <dd>{Object.entries(settings?.effective_controls || {}).filter(([key, value]) => key !== "mode" && value === true).map(([key]) => key.replace(/_enabled$/, "")).join(", ") || "none"}</dd>
          </div>
        </dl>
      )}

      <div className="grid md:grid-cols-[minmax(180px,240px)_1fr] gap-4">
        <label className="space-y-1 text-xs text-white/70">
          <span className="block text-white/45 uppercase tracking-wider text-[10px]">
            Mode
          </span>
          <select
            value={mode}
            disabled={settingsControlsDisabled}
            onChange={(event) => patchSettings({ mode: event.target.value })}
            data-testid="operator-automation-mode-select"
            className="w-full rounded bg-black/30 border border-white/15 px-3 py-2 text-sm text-white disabled:opacity-50"
          >
            {MODE_OPTIONS
              .filter(([value]) => {
                if (value === "trusted_auto" && !TRUSTED_MODE_OPERATOR_KEYS.has(operator.key)) return false;
                if (
                  isFeelGood
                  && ["apply_only", "apply_verify", "apply_verify_publish", "trusted_auto"].includes(value)
                ) return false;
                if (
                  isAspire
                  && ["apply_only", "apply_verify", "trusted_auto"].includes(value)
                ) return false;
                if (isLlf && ["apply_only", "apply_verify"].includes(value)) return false;
                if (isKilted && ["apply_only", "apply_verify"].includes(value)) return false;
                if (isMcKinney && ["apply_only", "apply_verify"].includes(value)) return false;
                return true;
              })
              .map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {ACTION_FIELDS
            .filter(([field]) => {
              if (isBotb) return BOTB_ACTION_FIELDS.has(field);
              if (isSevenDays) return SEVENDAYS_ACTION_FIELDS.has(field);
              if (isClick) return CLICK_ACTION_FIELDS.has(field);
              if (isJonnyFromDonny) return JFD_ACTION_FIELDS.has(field);
              if (isFeelGood) return FEELGOOD_ACTION_FIELDS.has(field);
              if (isAspire) return ASPIRE_ACTION_FIELDS.has(field);
              if (isMcKinney) return MCKINNEY_ACTION_FIELDS.has(field);
              if (isLlf) return LLF_ACTION_FIELDS.has(field);
              if (isKilted) return KILTED_ACTION_FIELDS.has(field);
              return true;
            })
            .map(([field, label]) => (
            <SettingToggle
              key={field}
              field={field}
              label={label}
              checked={settingChecked(field)}
              disabled={
                settingsControlsDisabled
                || locked
                || pendingBooleanSettings[field] === true
                || (
                  isRevComps
                  && ![
                    "scan_enabled",
                    "dry_run_enabled",
                    "apply_enabled",
                    "verify_enabled",
                    "publish_enabled",
                    "expire_enabled",
                  ].includes(field)
                )
              }
              dangerous={field === "publish_enabled" || field === "expire_enabled"}
              onChange={(checked) => patchSettings({ [field]: checked })}
            />
          ))}
          {!isJonnyFromDonny && !isFeelGood && (
            <SettingToggle
              field="scheduler_enabled"
              label="Scheduler"
              checked={settingChecked("scheduler_enabled")}
              disabled={settingsControlsDisabled || pendingBooleanSettings.scheduler_enabled === true || (isRevComps
                ? settings?.scheduler_enabled !== true && (
                    locked
                    || revReadiness?.ready !== true
                    || revReadiness?.public_reconciliation?.status !== "PASS"
                  )
                : (isAspire || isMcKinney || isLlf || isKilted)
                ? settings?.scheduler_enabled !== true && (
                    locked
                    || settings?.apply_enabled !== true
                    || settings?.verify_enabled !== true
                    || settings?.publish_enabled !== true
                    || (isMcKinney && settings?.mckinney_schedule_resolved !== true)
                  )
                : locked)}
              onChange={(checked) => patchSettings(
                isRevComps && checked
                  ? { scheduler_enabled: true, expire_enabled: false }
                  : { scheduler_enabled: checked },
              )}
            />
          )}
          <SettingToggle
            field="notifications_enabled"
            label="Notifications"
            checked={settingChecked("notifications_enabled")}
            disabled={settingsControlsDisabled || pendingBooleanSettings.notifications_enabled === true}
            onChange={(checked) => patchSettings({ notifications_enabled: checked })}
          />
        </div>
      </div>

      {isBotb && (
        <div className="grid md:grid-cols-2 gap-2">
          <div
            className="rounded border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] text-white/65"
            data-testid="operator-automation-botb-dry-run-readonly"
          >
            <span className="font-semibold text-white/80">Dry-run available:</span>
            {" "}always on. Dry-run versus live is selected by the action button, not this setting.
          </div>
          <div
            className="rounded border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] text-white/65"
            data-testid="operator-automation-botb-unsupported-phases-note"
          >
            BOTB uses the trusted public catalogue refresh path. Apply, Verify and Publish are disabled for BOTB.
          </div>
        </div>
      )}

      {isSevenDays && (
        <div
          className="rounded border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
          data-testid="operator-automation-7days-dry-run-note"
        >
          7days supports dry-run scan, draft apply, verify-only and publish-only. Apply
          creates or updates pending-review records; verify promotes existing
          draft records only; publish exposes verified drafts only. Expiry hides only
          records confirmed closed, sold or past by current 7days API detail evidence;
          Scheduler runs only when explicitly enabled.
        </div>
      )}

      {isClick && (
        <div
          className="rounded border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
          data-testid="operator-automation-click-dry-run-note"
        >
          Click Competitions supports dry-run scan, apply-only draft
          ingestion, verify-only promotion, publish-only for verified odds-safe
          drafts, guarded expiry and trusted scheduled refresh when explicitly
          enabled. Expiry never deletes raw records.
        </div>
      )}

      {isJonnyFromDonny && (
        <div
          className="rounded border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
          data-testid="operator-automation-jonnyfromdonny-dry-run-note"
        >
          Jonny From Donny feasibility scan is scan only - no records are
          created. It is not trusted automation yet, and Budget Odds remains
          disabled until extraction evidence is verified.
        </div>
      )}

      {isFeelGood && (
        <div
          className="rounded border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
          data-testid="operator-automation-feelgood-dry-run-note"
        >
          Feel Good Competitions feasibility scan is scan only - no records are
          created. It is not trusted automation yet, and Budget Odds remains
          disabled until extraction evidence is verified.
        </div>
      )}

      {isAspire && (
        <div
          className="rounded border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
          data-testid="operator-automation-aspire-dry-run-note"
        >
          Aspire supports immutable-snapshot preflight/apply, exact-set verify,
          exact-set publish, guarded managed-set expiry and a trusted scheduled
          sequence. Expiry and Scheduler remain disabled by default and must be
          enabled explicitly after the complete live-set checks pass.
        </div>
      )}

      {isMcKinney && (
        <div
          className="rounded border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
          data-testid="operator-automation-mckinney-lifecycle-note"
        >
          <div>
            McKinney supports a GBP-only dry-run scan, immutable-snapshot
            preflight/apply, exact-set verify/publish, guarded expiry and a
            deterministic scheduled sequence. Existing public records are preserved.
            Expiry and Scheduler stay off until explicitly enabled and their
            server-side safety gates pass.
          </div>
          <dl
            className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2"
            data-testid="operator-automation-mckinney-authoritative-status"
          >
            <div><dt className="text-white/45">Publish setting</dt><dd>{settings?.publish_enabled === true ? "enabled" : "disabled"}</dd></div>
            <div><dt className="text-white/45">Publish gate</dt><dd data-testid="mckinney-publish-gate-state">{settings?.mckinney_publish_gate_allowed === true ? "ready" : "blocked"}</dd></div>
            <div><dt className="text-white/45">Gate reason</dt><dd data-testid="mckinney-publish-gate-reasons">{(settings?.mckinney_publish_gate_reasons || []).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Expiry setting / gate</dt><dd data-testid="mckinney-expiry-gate-state">{settings?.expire_enabled === true ? "enabled" : "disabled"} / {settings?.mckinney_expiry_gate_allowed === true ? "ready" : "blocked"}</dd></div>
            <div><dt className="text-white/45">Expiry gate reasons</dt><dd data-testid="mckinney-expiry-gate-reasons">{(settings?.mckinney_expiry_gate_reasons || []).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Scheduler setting / gate</dt><dd data-testid="mckinney-scheduler-gate-state">{settings?.scheduler_enabled === true ? "enabled" : "disabled"} / {settings?.mckinney_scheduler_gate_allowed === true ? "ready" : "blocked"}</dd></div>
            <div><dt className="text-white/45">Scheduler gate reasons</dt><dd data-testid="mckinney-scheduler-gate-reasons">{(settings?.mckinney_scheduler_gate_reasons || []).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Notifications</dt><dd>{settings?.notifications_enabled === true ? "enabled" : "disabled"}</dd></div>
            <div><dt className="text-white/45">Schedule mode</dt><dd>{settings?.mckinney_schedule_mode || "unknown"}</dd></div>
            <div><dt className="text-white/45">Configured schedule</dt><dd>{settings?.schedule_hour == null ? "unresolved" : `${String(settings.schedule_hour).padStart(2, "0")}:${String(settings?.schedule_minute ?? 0).padStart(2, "0")}`} / {settings?.schedule_interval_hours ?? "?"}h / {settings?.schedule_timezone || "unknown"}</dd></div>
            <div><dt className="text-white/45">Resolved next run</dt><dd data-testid="mckinney-resolved-next-run">{settings?.mckinney_resolved_next_run_local || settings?.mckinney_resolved_next_run_at || settings?.mckinney_unresolved_schedule_reason || "unresolved"}</dd></div>
            <div><dt className="text-white/45">Schedule generation</dt><dd>{settings?.mckinney_schedule_generation ?? settings?.schedule_generation ?? "unknown"}</dd></div>
            <div><dt className="text-white/45">Latest claimed slot</dt><dd className="break-all">{settings?.latest_mckinney_claimed_schedule_slot || "none"}</dd></div>
            <div><dt className="text-white/45">Latest scheduled run</dt><dd className="break-all">{settings?.latest_mckinney_scheduled_run_id || "none"}</dd></div>
            <div><dt className="text-white/45">Latest complete cycle</dt><dd className="break-all">{settings?.latest_successful_mckinney_complete_cycle_id || "none"}</dd></div>
            <div><dt className="text-white/45">Cycle status</dt><dd>{settings?.current_mckinney_scheduled_cycle_status || settings?.latest_mckinney_scheduled_cycle_status || "none"}</dd></div>
            <div><dt className="text-white/45">Current phase</dt><dd>{settings?.current_mckinney_phase || "none"}</dd></div>
            <div><dt className="text-white/45">Stop reason</dt><dd data-testid="mckinney-lifecycle-stop-reason">{settings?.current_mckinney_lifecycle_stop_reason || settings?.latest_mckinney_lifecycle_stop_reason || "none"}</dd></div>
            <div><dt className="text-white/45">Phase lineage</dt><dd className="break-all">{Object.entries(settings?.current_mckinney_phase_lineage || settings?.latest_mckinney_phase_lineage || {}).map(([phase, value]) => `${phase}:${value?.status || "unknown"}`).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Execution lease</dt><dd data-testid="mckinney-execution-lease-state">{settings?.execution_lease?.active === true ? "active" : "inactive"} / {settings?.execution_lease?.execution_type || "none"}</dd></div>
            <div><dt className="text-white/45">Lease run / owner</dt><dd className="break-all">{settings?.execution_lease?.run_id || "none"} / {settings?.execution_lease?.owner || "none"}</dd></div>
            <div><dt className="text-white/45">Heartbeat / expiry</dt><dd>{settings?.execution_lease?.heartbeat_at || "none"} / {settings?.execution_lease?.lease_expires_at || "none"}</dd></div>
            <div><dt className="text-white/45">Verified live set</dt><dd className="break-all">{settings?.mckinney_publish_gate_live_set_id || settings?.latest_successful_mckinney_live_set_id || "missing"}</dd></div>
            <div><dt className="text-white/45">Live-set count</dt><dd>{settings?.mckinney_publish_gate_identity_count ?? settings?.latest_successful_mckinney_live_set_identity_count ?? "unknown"}</dd></div>
            <div><dt className="text-white/45">Identity digest</dt><dd className="break-all">{settings?.mckinney_publish_gate_identity_digest || settings?.latest_successful_mckinney_live_set_identity_digest || "missing"}</dd></div>
            <div><dt className="text-white/45">Expiry approval</dt><dd data-testid="mckinney-expiry-approval-state">{settings?.mckinney_expiry_approval_state || "unavailable"} / {settings?.mckinney_expiry_approval_execution_type || "unknown"}</dd></div>
            <div><dt className="text-white/45">Approval TTL</dt><dd>{settings?.mckinney_expiry_approval_ttl_seconds == null ? "unknown" : `${settings.mckinney_expiry_approval_ttl_seconds}s`}</dd></div>
            <div><dt className="text-white/45">Approval live set</dt><dd className="break-all">{settings?.mckinney_expiry_approval_live_set_id || "missing"} / {settings?.mckinney_expiry_approval_live_set_count ?? "?"}</dd></div>
            <div><dt className="text-white/45">Retained identities</dt><dd className="break-all">{settings?.mckinney_expiry_approval_retained_count ?? 0}: {(settings?.mckinney_expiry_approval_retained_ids || []).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Expiry candidates</dt><dd className="break-all">{settings?.mckinney_expiry_approval_candidate_count ?? 0}: {(settings?.mckinney_expiry_approval_candidate_ids || []).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Candidate digest / ceiling</dt><dd className="break-all">{settings?.mckinney_expiry_approval_candidate_digest || "missing"} / {settings?.mckinney_expiry_approval_candidate_ceiling ?? "?"}</dd></div>
            <div><dt className="text-white/45">Protected identities</dt><dd className="break-all">{settings?.mckinney_expiry_approval_protected_count ?? 0}: {(settings?.mckinney_expiry_approval_protected_ids || []).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Protected reasons</dt><dd>{Object.entries(settings?.mckinney_expiry_approval_protected_reasons || {}).map(([key, value]) => `${key}: ${value}`).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Missing / unexpected approval IDs</dt><dd>{(settings?.mckinney_expiry_approval_missing_ids || []).length} / {(settings?.mckinney_expiry_approval_unexpected_ids || []).length}</dd></div>
            <div><dt className="text-white/45">Approval accounting</dt><dd>{settings?.mckinney_expiry_approval_exact_set_accounted === true ? "exact" : "blocked"}</dd></div>
            <div><dt className="text-white/45">Public before / after</dt><dd>{settings?.mckinney_expiry_approval_expected_public_before ?? "?"} / {settings?.mckinney_expiry_approval_expected_public_after ?? "?"}</dd></div>
            <div><dt className="text-white/45">Public expected / actual</dt><dd>{settings?.mckinney_public_projection_expected_count ?? "?"} / {settings?.mckinney_public_projection_actual_count ?? "?"}</dd></div>
            <div><dt className="text-white/45">Public reconciliation</dt><dd data-testid="mckinney-public-projection-state">{settings?.mckinney_public_projection_reconciled === true ? "PASS" : "BLOCKED"}</dd></div>
            <div><dt className="text-white/45">Missing / unexpected</dt><dd>{settings?.mckinney_public_projection_missing_count ?? "?"} / {settings?.mckinney_public_projection_unexpected_count ?? "?"}</dd></div>
            <div><dt className="text-white/45">Historical / review leaks</dt><dd>{settings?.mckinney_public_projection_historical_leak_count ?? "?"} / {settings?.mckinney_public_projection_review_excluded_leak_count ?? "?"}</dd></div>
            <div><dt className="text-white/45">Operator filter</dt><dd>{settings?.mckinney_public_projection_operator_filter_exposed === true ? "exposed" : "not exposed"}</dd></div>
            <div><dt className="text-white/45">Categories</dt><dd>{Object.entries(settings?.mckinney_public_projection_category_counts || {}).map(([key, value]) => `${key}: ${value}`).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Projection checked</dt><dd>{settings?.mckinney_public_projection_checked_at || "not checked"}</dd></div>
            <div><dt className="text-white/45">Latest phase</dt><dd>{settings?.mckinney_latest_phase_outcome_phase || "none"}: {settings?.mckinney_latest_phase_outcome_status || "unknown"}</dd></div>
            <div><dt className="text-white/45">Health / shadow</dt><dd>{settings?.mckinney_authoritative_health_state || "unknown"} / {settings?.mckinney_shadow_health_state || "unknown"}</dd></div>
            <div><dt className="text-white/45">Notification outcome</dt><dd data-testid="mckinney-notification-outcome">{settings?.latest_scheduled_notification_outcome?.status || "none"}: {settings?.latest_scheduled_notification_outcome?.notification_outcome_class || "none"}</dd></div>
          </dl>
        </div>
      )}

      {isLlf && (
        <div
          className="rounded border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
          data-testid="operator-automation-llf-lifecycle-note"
        >
          <div>
            LLF uses exact product identities, an immutable Scan snapshot,
            transactional Apply, exact-set Verify and Publish, guarded
            same-product Expiry, and scheduled maintenance. Publish, Expiry
            and Scheduler remain disabled by default.
          </div>
          <dl
            className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2"
            data-testid="operator-automation-llf-authoritative-status"
          >
            <div><dt className="text-white/45">Snapshot</dt><dd className="break-all">{settings?.latest_llf_scan_snapshot_id || "missing"}</dd></div>
            <div><dt className="text-white/45">Snapshot state</dt><dd>{settings?.latest_llf_scan_snapshot_state || "unknown"}</dd></div>
            <div><dt className="text-white/45">Publish setting</dt><dd>{settings?.publish_enabled === true ? "enabled" : "disabled"}</dd></div>
            <div><dt className="text-white/45">Publish gate</dt><dd data-testid="llf-publish-gate-state">{settings?.llf_publish_gate_allowed === true ? "ready" : "blocked"}</dd></div>
            <div><dt className="text-white/45">Gate reasons</dt><dd data-testid="llf-publish-gate-reasons">{(settings?.llf_publish_gate_reasons || []).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Verified live set</dt><dd className="break-all">{settings?.llf_publish_gate_live_set_id || settings?.latest_successful_llf_live_set_id || "missing"}</dd></div>
            <div><dt className="text-white/45">Live-set count</dt><dd>{settings?.llf_publish_gate_identity_count ?? settings?.latest_successful_llf_live_set_identity_count ?? "unknown"}</dd></div>
            <div><dt className="text-white/45">Identity digest</dt><dd className="break-all">{settings?.llf_publish_gate_identity_digest || settings?.latest_successful_llf_live_set_identity_digest || "missing"}</dd></div>
            <div><dt className="text-white/45">Public expected / actual</dt><dd>{settings?.llf_public_projection_expected_count ?? "?"} / {settings?.llf_public_projection_actual_count ?? "?"}</dd></div>
            <div><dt className="text-white/45">Missing / unexpected</dt><dd>{settings?.llf_public_projection_missing_count ?? "?"} / {settings?.llf_public_projection_unexpected_count ?? "?"}</dd></div>
            <div><dt className="text-white/45">Historical / review / ineligible leaks</dt><dd>{settings?.llf_public_projection_historical_leak_count ?? "?"} / {settings?.llf_public_projection_review_excluded_leak_count ?? "?"} / {settings?.llf_public_projection_ineligible_leak_count ?? "?"}</dd></div>
            <div><dt className="text-white/45">Operator filter</dt><dd>{settings?.llf_public_projection_operator_filter_exposed === true ? "exposed" : "not exposed"}</dd></div>
            <div><dt className="text-white/45">Categories</dt><dd>{Object.entries(settings?.llf_public_projection_category_counts || {}).map(([key, value]) => `${key}: ${value}`).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Projection checked</dt><dd>{settings?.llf_public_projection_checked_at || "not checked"}</dd></div>
            <div><dt className="text-white/45">Latest phase</dt><dd>{settings?.llf_latest_phase_outcome_phase || "none"}: {settings?.llf_latest_phase_outcome_status || "unknown"}</dd></div>
            <div><dt className="text-white/45">Health / shadow</dt><dd>{settings?.llf_authoritative_health_state || "unknown"} / {settings?.llf_shadow_health_state || "unknown"}</dd></div>
            <div><dt className="text-white/45">Expiry / Scheduler</dt><dd>{settings?.expire_enabled === true ? "enabled" : "disabled"} / {settings?.scheduler_enabled === true ? "enabled" : "disabled"}</dd></div>
          </dl>
        </div>
      )}

      {isExactSetOperator && (
        <div
          className="rounded border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
          data-testid={`operator-automation-${operatorKey}-lifecycle-note`}
        >
          <div>
            {exactSetOperatorName} uses exact product identities, an immutable Scan snapshot,
            transactional Apply, exact-set Verify and Publish, guarded
            same-product Expiry, and scheduled maintenance. Every capability
            remains disabled until explicitly enabled.
          </div>
          <dl
            className="mt-3 grid sm:grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-2"
            data-testid={`operator-automation-${operatorKey}-authoritative-status`}
          >
            <div><dt className="text-white/45">Snapshot</dt><dd className="break-all">{settings?.[`latest_${exactSetPrefix}_scan_snapshot_id`] || "missing"}</dd></div>
            <div><dt className="text-white/45">Snapshot state</dt><dd>{settings?.[`latest_${exactSetPrefix}_scan_snapshot_state`] || "unknown"}</dd></div>
            <div><dt className="text-white/45">Publish setting</dt><dd>{settings?.publish_enabled === true ? "enabled" : "disabled"}</dd></div>
            <div><dt className="text-white/45">Publish gate</dt><dd data-testid={`${operatorKey}-publish-gate-state`}>{settings?.[`${exactSetPrefix}_publish_gate_allowed`] === true ? "ready" : "blocked"}</dd></div>
            <div><dt className="text-white/45">Gate reasons</dt><dd>{(settings?.[`${exactSetPrefix}_publish_gate_reasons`] || []).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Verified live set</dt><dd className="break-all" data-testid={`${operatorKey}-verified-live-set`}>{exactSetLiveSetId || "missing"}</dd></div>
            <div><dt className="text-white/45">Live-set count</dt><dd data-testid={`${operatorKey}-verified-live-set-count`}>{exactSetIdentityCount ?? "unknown"}</dd></div>
            <div><dt className="text-white/45">Identity digest</dt><dd className="break-all" data-testid={`${operatorKey}-verified-live-set-digest`}>{exactSetIdentityDigest || "missing"}</dd></div>
            <div><dt className="text-white/45">Public expected / actual</dt><dd>{settings?.[`${exactSetPrefix}_public_projection_expected_count`] ?? "?"} / {settings?.[`${exactSetPrefix}_public_projection_actual_count`] ?? "?"}</dd></div>
            <div><dt className="text-white/45">Missing / unexpected</dt><dd>{settings?.[`${exactSetPrefix}_public_projection_missing_count`] ?? "?"} / {settings?.[`${exactSetPrefix}_public_projection_unexpected_count`] ?? "?"}</dd></div>
            <div><dt className="text-white/45">Historical / review / ineligible leaks</dt><dd>{settings?.[`${exactSetPrefix}_public_projection_historical_leak_count`] ?? "?"} / {settings?.[`${exactSetPrefix}_public_projection_review_excluded_leak_count`] ?? "?"} / {settings?.[`${exactSetPrefix}_public_projection_ineligible_leak_count`] ?? "?"}</dd></div>
            <div><dt className="text-white/45">Operator filter</dt><dd>{settings?.[`${exactSetPrefix}_public_projection_operator_filter_exposed`] === true ? "exposed" : "not exposed"}</dd></div>
            <div><dt className="text-white/45">Categories</dt><dd>{Object.entries(settings?.[`${exactSetPrefix}_public_projection_category_counts`] || {}).map(([key, value]) => `${key}: ${value}`).join(", ") || "none"}</dd></div>
            <div><dt className="text-white/45">Projection checked</dt><dd>{settings?.[`${exactSetPrefix}_public_projection_checked_at`] || "not checked"}</dd></div>
            <div><dt className="text-white/45">Latest phase</dt><dd>{settings?.[`${exactSetPrefix}_latest_phase_outcome_phase`] || "none"}: {settings?.[`${exactSetPrefix}_latest_phase_outcome_status`] || "unknown"}</dd></div>
            <div><dt className="text-white/45">Health / shadow</dt><dd>{settings?.[`${exactSetPrefix}_authoritative_health_state`] || "unknown"} / {settings?.[`${exactSetPrefix}_shadow_health_state`] || "unknown"}</dd></div>
            <div><dt className="text-white/45">Expiry / Scheduler</dt><dd>{settings?.expire_enabled === true ? "enabled" : "disabled"} / {settings?.scheduler_enabled === true ? "enabled" : "disabled"}</dd></div>
          </dl>
        </div>
      )}

      {locked && (
        <div
          className="rounded border border-amber-400/25 bg-amber-400/10 px-3 py-2 text-[11px] text-amber-100"
          data-testid="operator-automation-locked-note"
        >
          <ShieldWarning size={14} className="inline mr-1" />
          Action toggles are disabled while this operator is {mode.replace("_", " ")}.
          Change the mode first before enabling run controls.
        </div>
      )}

      {isRevComps && (
        <div
          className="rounded border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
          data-testid="operator-automation-rev-apply-only-note"
        >
          <div>Rev Comps supports trusted scheduled Scan, Apply, Verify and Publish
          at 06:30 Europe/London when Scheduler is explicitly enabled. Expiry remains
          disabled and is never invoked by the scheduled sequence.</div>
          <div className="mt-2 flex items-center gap-2 flex-wrap">
            <button
              type="button"
              onClick={checkRevReadiness}
              disabled={loading || revReadinessBusy}
              data-testid="operator-automation-revcomps-readiness-btn"
              className="px-3 py-1.5 rounded border border-sky-300/30 bg-sky-300/10 disabled:opacity-45"
            >
              {revReadinessBusy ? "Checking readiness..." : "Check Rev scheduler readiness"}
            </button>
            <strong data-testid="operator-automation-revcomps-readiness-status">
              {revReadiness ? revReadiness.readiness : "NOT CHECKED"}
            </strong>
          </div>
          {revReadiness && (
            <div className="mt-2 space-y-1" data-testid="operator-automation-revcomps-readiness-diagnostics">
              <div>
                Public reconciliation: {revReadiness.public_reconciliation?.status || "BLOCKED"}
              </div>
              <div>
                Effectively public: {revReadiness.public_reconciliation?.rev_effective_public_count ?? "Unavailable"}
              </div>
              <div>
                Published but hidden: {revReadiness.public_reconciliation?.rev_hidden_published_count ?? "Unavailable"}
              </div>
              <div>
                Current-live effectively public: {revReadiness.public_reconciliation?.rev_current_live_effective_public_count ?? "Unavailable"}
              </div>
              <div>
                Unmanaged effectively public: {revReadiness.public_reconciliation?.rev_unmanaged_effective_public_count ?? "Unavailable"}
              </div>
              <div>
                Expiry candidates: {revReadiness.public_reconciliation?.rev_prior_live_set_effective_public_candidate_count ?? "Unavailable"}
              </div>
              <div>
                Expected effectively public after expiry: {revReadiness.public_reconciliation?.rev_expected_effective_public_count_after_expiry ?? "Unavailable"}
              </div>
              <div>Phase order: {(revReadiness.scheduler_phase_order || []).join(" -> ")}</div>
              <div>Halts after phase failure: {revReadiness.scheduler_halts_on_phase_failure ? "Yes" : "No"}</div>
              <div>Next UTC: {revReadiness.next_scheduled_run_utc || "Unavailable"}</div>
              <div>Next Europe/London: {revReadiness.next_scheduled_run_local || "Unavailable"}</div>
              {(revReadiness.blocked_reasons || []).length > 0 && (
                <div>Blocked reasons: {revReadiness.blocked_reasons.join(", ")}</div>
              )}
              {(revReadiness.public_reconciliation?.failure_reasons || []).length > 0 && (
                <div>
                  Reconciliation failures: {revReadiness.public_reconciliation.failure_reasons.join(", ")}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {isBotb && (
        <div
          className="rounded border border-sky-400/20 bg-sky-400/10 px-3 py-2 text-[11px] text-sky-100"
          data-testid="operator-automation-botb-scan-only-note"
        >
          BOTB settings are editable for admin diagnostics, public catalogue
          refresh, guarded live expiry and scheduled refresh/expiry. Live expiry
          hides stale public rows only and never deletes raw records. Backend
          safety still blocks generic apply, verify, publish, affiliate links
          and cart/purchase endpoints.
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <NumberSetting
          label="Max new"
          value={settings?.max_new_records}
          disabled={settingsControlsDisabled}
          onCommit={(value) => patchSettings({ max_new_records: value })}
        />
        <NumberSetting
          label="Max expire"
          value={settings?.max_expire_records}
          disabled={settingsControlsDisabled}
          onCommit={(value) => patchSettings({ max_expire_records: value })}
        />
        <NumberSetting
          label="Min cards"
          value={settings?.min_cards_discovered}
          disabled={settingsControlsDisabled}
          onCommit={(value) => patchSettings({ min_cards_discovered: value })}
        />
        <NumberSetting
          label="Min publishable"
          value={settings?.min_publishable_records}
          disabled={settingsControlsDisabled}
          onCommit={(value) => patchSettings({ min_publishable_records: value })}
        />
        {!isJonnyFromDonny && !isFeelGood && (
          <>
            <NumberSetting
              label="Schedule hour"
              value={settings?.schedule_hour}
              disabled={settingsControlsDisabled || locked}
              min={0}
              max={23}
              testId="operator-automation-schedule-hour-input"
              onCommit={(value) => patchSettings({ schedule_hour: value })}
            />
            <NumberSetting
              label="Schedule minute"
              value={settings?.schedule_minute}
              disabled={settingsControlsDisabled || locked}
              min={0}
              max={59}
              testId="operator-automation-schedule-minute-input"
              onCommit={(value) => patchSettings({ schedule_minute: value })}
            />
            <NumberSetting
              label="Cadence hours"
              value={settings?.schedule_interval_hours}
              disabled={settingsControlsDisabled || locked}
              min={1}
              max={24}
              testId="operator-automation-schedule-interval-input"
              onCommit={(value) => patchSettings({ schedule_interval_hours: value })}
            />
          </>
        )}
      </div>

      {!isJonnyFromDonny && !isFeelGood && (
        <>
          <div
            className="rounded border border-white/10 bg-white/[0.03] px-3 py-2 text-[11px] text-white/65"
            data-testid="operator-automation-schedule-cadence"
          >
            <span className="font-semibold text-white/80">
              {isAspire ? "Scheduled lifecycle:" : "Trusted automation only:"}
            </span>
            {" "}Base time {String(settings?.schedule_hour ?? "--").padStart(2, "0")}:
            {String(settings?.schedule_minute ?? "00").padStart(2, "0")}
            {" "}in {settings?.schedule_timezone || "Europe/London"}. Runs every{" "}
            {settings?.schedule_interval_hours ?? 24} hours from base time.
            {settings?.last_scheduled_run_next_at && (
              <>
                {" "}Next scheduled refresh:{" "}
                {new Date(settings.last_scheduled_run_next_at).toLocaleString("en-GB")}.
              </>
            )}
          </div>

          <label className="block text-xs text-white/60">
            <span className="inline-flex items-center gap-1 text-white/45 uppercase tracking-wider text-[10px] mb-1">
              <Clock size={12} /> Schedule timezone
            </span>
            <input
              type="text"
              key={settings?.schedule_timezone || ""}
              defaultValue={settings?.schedule_timezone || ""}
              disabled={settingsControlsDisabled || locked}
              onBlur={(event) => {
                const value = event.target.value.trim();
                if (value && value !== settings?.schedule_timezone) {
                  patchSettings({ schedule_timezone: value });
                }
              }}
              data-testid="operator-automation-timezone-input"
              className="w-full rounded bg-black/25 border border-white/10 px-3 py-2 text-xs text-white/80 disabled:opacity-50"
            />
          </label>
        </>
      )}

      <div className="rounded border border-white/10 bg-black/10 p-3 text-[11px] text-white/55">
        <div className="flex items-center gap-1.5 text-white/75 font-semibold mb-1">
          <Bell size={13} /> Last run
        </div>
        {settings?.last_run_at ? (
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span>{new Date(settings.last_run_at).toLocaleString("en-GB")}</span>
            <span>Status: {settings.last_run_status || "unknown"}</span>
            <span>Scanned: {lastSummary.scanned ?? 0}</span>
            <span>Created: {lastSummary.created ?? 0}</span>
            <span>Updated: {lastSummary.updated ?? 0}</span>
            <span>Published: {lastSummary.auto_published ?? 0}</span>
            <span>Hidden: {lastSummary.expired_hidden ?? 0}</span>
            <span>Errors: {lastSummary.errors ?? 0}</span>
            {settings?.last_scheduled_run_at && (
              <span>
                Scheduled: {new Date(settings.last_scheduled_run_at).toLocaleString("en-GB")}
                {" "}({settings.last_scheduled_run_status || "unknown"})
              </span>
            )}
          </div>
        ) : (
          <span>
            {settings?.last_scheduled_run_at
              ? `Last scheduled run: ${new Date(settings.last_scheduled_run_at).toLocaleString("en-GB")} (${settings.last_scheduled_run_status || "unknown"})`
              : "No run recorded yet."}
          </span>
        )}
      </div>
    </div>
  );
}

function SettingToggle({ field, label, checked, disabled, dangerous, onChange }) {
  return (
    <label
      className={`rounded border px-3 py-2 text-xs flex items-center justify-between gap-2 ${
        checked
          ? dangerous
            ? "border-red-400/35 bg-red-400/10 text-red-100"
            : "border-emerald-400/30 bg-emerald-400/10 text-emerald-100"
          : "border-white/10 bg-white/[0.03] text-white/55"
      } ${disabled ? "opacity-55" : ""}`}
      data-testid={`operator-automation-toggle-${field}`}
    >
      <span>{label}</span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
    </label>
  );
}

function NumberSetting({ label, value, disabled, min = 0, max = null, testId, onCommit }) {
  return (
    <label className="space-y-1 text-xs text-white/60">
      <span className="block text-white/45 uppercase tracking-wider text-[10px]">
        {label}
      </span>
      <input
        type="number"
        min={min}
        max={max == null ? undefined : max}
        key={value ?? ""}
        defaultValue={value ?? ""}
        disabled={disabled}
        onBlur={(event) => {
          const parsed = parseNumberSettingValue(event.target.value, {
            min,
            max,
            label,
          });
          if (parsed.skip) return;
          if (parsed.error) {
            toast.error(parsed.error);
            event.target.value = value ?? "";
            return;
          }
          onCommit(parsed.value);
        }}
        data-testid={testId}
        className="w-full rounded bg-black/25 border border-white/10 px-3 py-2 text-xs text-white/80 disabled:opacity-50"
      />
    </label>
  );
}
